import { type BlockTableData, type TableView, table } from '@gitbook/doc-core';
import { PaletteCommandType, type PaletteDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { type EditorContainer, useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';

import { useBlockPaletteFactory } from '../common';
import { createColumnDefsPalette } from './ColumnDefinitions';
import { type TableViewCommandsParams, viewSpecs } from './Views';

/**
 * Create the command palette to edit a table
 */
export function useTablePaletteFactory(
    nodeKey: string,
    options?: {
        createExtraGroups?: () => PaletteGroupDef[];
    }
): () => PaletteDef {
    const editor = useEditor();

    const createTablePalette = useBlockPaletteFactory({
        nodeKey,
        createExtraGroups: () => {
            const block = editor.value.document.assertBlock(nodeKey);
            const tableData = block.data.toFrozenObject<BlockTableData>();
            const viewSpec = viewSpecs[tableData.view.type];
            const extraGroups = options?.createExtraGroups ? options.createExtraGroups() : [];

            return [
                {
                    key: 'views',
                    commands: Object.entries(viewSpecs).map(([key, spec]) => {
                        return {
                            key,
                            title: spec.title,
                            icon: spec.icon,
                            type: PaletteCommandType.CHECKBOX,
                            selected: key === tableData.view.type,
                            trigger: () => {
                                editor.change((change) => {
                                    table.setView(change, nodeKey, {
                                        type: key as TableView['type'],
                                    });
                                });
                            },
                        };
                    }),
                },
                {
                    key: 'table',
                    commands: [
                        {
                            key: 'addColumn',
                            icon: Icon.ArrowRight,
                            title: `Add ${viewSpec.definitionName}`,
                            trigger: () => {
                                editor.change((change) => {
                                    table.insertColumn(
                                        change,
                                        nodeKey,
                                        table.createColumnFromType('text')
                                    );
                                });
                            },
                            descendant: () => {
                                return createColumnDefsPalette((type) => {
                                    editor.change((change) => {
                                        table.insertColumn(
                                            change,
                                            nodeKey,
                                            table.createColumnFromType(type)
                                        );
                                    });
                                });
                            },
                        },
                        {
                            key: 'insertRow',
                            icon: Icon.ArrowDown,
                            title: `Insert ${viewSpec.recordName}`,
                            trigger: () => {
                                editor.change((change) => {
                                    table.insertEmptyRecord(change, nodeKey);
                                });
                            },
                        },
                    ],
                },
                ...(viewSpec.commands
                    ? viewSpec.commands(
                          createTableViewCommandsParams({
                              editor,
                              tableData: tableData as BlockTableData,
                              nodeKey,
                              createTablePalette,
                          })
                      )
                    : []),
                ...extraGroups,
            ];
        },
        search: { placeholder: 'Select action…' },
    });

    return createTablePalette;
}

/**
 * Create the parameter for the view palette.
 */
function createTableViewCommandsParams(input: {
    editor: EditorContainer;
    tableData: BlockTableData;
    nodeKey: string;
    createTablePalette: () => PaletteDef;
}): TableViewCommandsParams {
    const { editor, tableData, nodeKey, createTablePalette } = input;
    return {
        view: tableData.view,
        definition: tableData.definition,
        setView: (newView) => {
            editor.change((change) => {
                table.setView(change, nodeKey, newView);
            });

            const updatedTable = editor.value.document.assertBlock(nodeKey);

            return [
                createTablePalette,
                createTableViewCommandsParams({
                    editor,
                    tableData: updatedTable.data.toFrozenObject<BlockTableData>() as BlockTableData,
                    nodeKey,
                    createTablePalette,
                }),
            ];
        },
    };
}
